import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { 
  BookOpenIcon,
  MessageSquareIcon,
  TicketIcon,
  ClipboardListIcon,
  UsersIcon,
  ArrowRightIcon,
  RefreshCwIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  UserIcon,
  HelpCircleIcon,
  HeadphonesIcon,
  FileTextIcon,
  ExternalLinkIcon,
  PlusIcon,
  ReceiptIcon,
  BuildingIcon,
  MailIcon,
  SparklesIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Help = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Solo planı bireysel kullanıcı deneyimi — kurumsal referansları
  // (Şirket ID, Kiracı No, "şirket hesabı" dili) ekrandan tamamen kaldırır
  // ve Solo iş akışına özel hızlı-başlangıç kartları gösterir. Diğer
  // planlar mevcut UI'yı görmeye devam eder.
  const isSoloPlan =
    (user?.company?.subscription_plan || '').toLowerCase() === 'solo';
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: 'technical',
    priority: 'medium',
    description: ''
  });
  const [processing, setProcessing] = useState(false);
  // Solo Hızlı Yardım modalı — quick-action kartlarına tıklandığında
  // ilgili konunun adım-adım rehberi açılır (Documentation.js'e gitmeye
  // gerek olmadan). Her makalenin sonunda "Şimdi Dene" butonu ilgili
  // feature'a yönlendirir.
  const [soloHelpArticle, setSoloHelpArticle] = useState(null);
  const soloHelpArticles = {
    'new-expense': {
      title: 'Yeni Harcama Nasıl Eklenir?',
      icon: ReceiptIcon,
      readTime: '2 dk',
      route: '/dashboard/expenses/new',
      cta: 'Şimdi Harcama Ekle',
      steps: [
        'Sol menüden "Yeni Harcama" seçin veya Dashboard\'daki hızlı ekle kartına tıklayın.',
        'Tarih, tutar ve para birimini (TRY / USD / EUR) girin.',
        'Kategoriyi seçin — Genel Gider veya Fatura (Elektrik, Su, Internet vb.).',
        'İşletme adını yazın. Sistem daha önce kullandığınız isimleri otomatik tamamlar.',
        'İsteğe bağlı: Açıklama ekleyin ve fişin/faturanın fotoğrafını yükleyin.',
        '"Kaydet"e tıklayın — harcamanız anında "Tüm Harcamalarım" listesine düşer.',
      ],
      tips: [
        'Aynı işletmeye 2. kez harcama yazarken sistem uyarır → mükerrer kayıt engellenir.',
        'Fiş fotoğrafını yüklerseniz aylık raporda otomatik olarak muhasebecinize gönderilir.',
      ],
    },
    'suppliers': {
      title: 'İşletmeler Nasıl Kullanılır?',
      icon: BuildingIcon,
      readTime: '3 dk',
      route: '/dashboard/suppliers',
      cta: 'İşletmeler’i Aç',
      steps: [
        'Sol menüden "İşletmeler" bölümüne girin.',
        'Tüm işletmeleriniz — kullanım sayısı ve toplam harcama ile birlikte — listelenir.',
        'Bir işletmeye tıklayarak sadece o işletmeden yaptığınız harcamaları görün.',
        '"Yıllık Grafik" ikonuna basarak son 12 aylık trendi + geçen yıl karşılaştırmasını görüntüleyin.',
        'İsim yanlış yazılmışsa "Düzenle" ile tek adımda tüm eski kayıtlara yansıyacak şekilde güncelleyebilirsiniz.',
      ],
      tips: [
        'Sistem işletmeyi otomatik oluşturur — yeni bir işletme eklemek için ayrı form doldurmanıza gerek yok.',
        'YoY (yıldan-yıla) grafik hangi işletmenin bütçenizde büyüdüğünü net gösterir.',
      ],
    },
    'export-report': {
      title: 'Muhasebeciye Rapor E-postası Nasıl Gönderilir?',
      icon: MailIcon,
      readTime: '2 dk',
      route: '/dashboard/expenses',
      cta: 'Rapor Sayfasını Aç',
      steps: [
        'Sol menüden "Tüm Harcamalarım" sayfasına gidin.',
        'Sağ üstteki "Dışa Aktar" veya "Rapora Gönder" butonuna tıklayın.',
        'Tarih aralığını seçin — ay, çeyrek veya özel aralık.',
        'Formatı seçin: PDF (özet + fişler) veya CSV (Excel için).',
        'Muhasebecinizin e-posta adresini girin (önceki gönderim varsa otomatik dolar).',
        '"Gönder"e tıklayın — rapor SendGrid üzerinden anlık iletilir.',
      ],
      tips: [
        'Ayarlar > Solo Ayarları kısmından varsayılan muhasebeci e-postasını kaydedebilirsiniz.',
        'Aylık otomatik gönderim yakında geliyor — her ayın 1\'inde tek tuşla planlanabilecek.',
      ],
    },
  };

  // Fetch user tickets
  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/support/tickets`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Destek talepleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  // Submit new ticket
  const submitTicket = async () => {
    if (!ticketForm.subject.trim() || !ticketForm.description.trim()) {
      toast.error('Lütfen konu ve açıklama alanlarını doldurun');
      return;
    }

    try {
      setProcessing(true);
      
      const response = await axios.post(`${API}/support/tickets`, {
        subject: ticketForm.subject,
        category: ticketForm.category,
        priority: ticketForm.priority,
        description: ticketForm.description
      });
      
      toast.success('Destek talebi başarıyla oluşturuldu');
      setShowTicketModal(false);
      setTicketForm({
        subject: '',
        category: 'technical',
        priority: 'medium',
        description: ''
      });
      
      // Refresh tickets list
      await fetchTickets();
      
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error(error.response?.data?.detail || 'Destek talebi oluşturulurken hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { variant: 'secondary', text: 'Açık', className: 'bg-blue-100 text-blue-800' },
      in_progress: { variant: 'secondary', text: 'İşlemde', className: 'bg-yellow-100 text-yellow-800' },
      resolved: { variant: 'secondary', text: 'Çözüldü', className: 'bg-green-100 text-green-800' },
      closed: { variant: 'secondary', text: 'Kapatıldı', className: 'bg-gray-100 text-gray-800' }
    };
    
    const config = statusConfig[status] || statusConfig.open;
    return (
      <Badge className={config.className}>
        {config.text}
      </Badge>
    );
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { text: 'Düşük', className: 'bg-gray-100 text-gray-800' },
      medium: { text: 'Orta', className: 'bg-blue-100 text-blue-800' },
      high: { text: 'Yüksek', className: 'bg-orange-100 text-orange-800' },
      critical: { text: 'Kritik', className: 'bg-red-100 text-red-800' }
    };
    
    const config = priorityConfig[priority] || priorityConfig.medium;
    return (
      <Badge className={config.className}>
        {config.text}
      </Badge>
    );
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header — Solo için samimi & bireysel dil + üst-sağda "Bize Yazın" CTA */}
      {isSoloPlan ? (
        <div className="flex items-start justify-between gap-6 py-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-4xl font-bold text-gray-900">Merhaba! Yardımcı olalım</h1>
            <p className="text-xl text-gray-600 max-w-2xl mt-4">
              Harcamalarınızı, işletmelerinizi ve raporlarınızı yönetirken takıldığınız her yerde buradayız. Solo planı ihtiyaçlarınıza özel hazırlandı.
            </p>
          </div>
          <div className="flex-shrink-0 pt-2">
            <Button
              onClick={() => setShowTicketModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg shadow-sm flex items-center gap-2"
              data-testid="solo-help-contact-btn"
            >
              <HeadphonesIcon className="w-4 h-4" />
              Bize Yazın
            </Button>
            <p className="text-[11px] text-gray-400 mt-1.5 text-right">Ekibimiz 24 saat içinde yanıtlar</p>
          </div>
        </div>
      ) : (
        <div className="text-center space-y-4 py-8">
          <h1 className="text-4xl font-bold text-gray-900">Yardım & Destek</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Advance Cash ile harika avans yönetimi deneyimi yaşamak için ihtiyacınız olan yardımı alın.
          </p>
        </div>
      )}

      {/* Solo: Bireysel Destek kutusu (Şirket ID yok). Diğer planlar: Şirket
          Özel Destek. */}
      {isSoloPlan ? (
        <Card className="p-6 bg-teal-50 border-teal-200" data-testid="solo-help-personal-support">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-lg bg-white border border-teal-200 flex items-center justify-center flex-shrink-0">
              <SparklesIcon className="w-5 h-5 text-teal-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-teal-900 mb-1">Solo Kişisel Destek</h3>
              <p className="text-teal-700 text-sm">
                Hesabınız: <code className="bg-teal-100 px-2 py-0.5 rounded text-xs font-mono">{user?.email}</code>
              </p>
              <p className="text-teal-700 text-sm mt-2">
                Bir talep açtığınızda e-posta adresiniz otomatik olarak eklenir.
                Ekibimiz genellikle <strong>24 saat içinde</strong> yanıt verir.
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="p-6 bg-teal-50 border-teal-200">
          <h3 className="text-lg font-semibold text-teal-900 mb-2">Şirket Özel Destek</h3>
          <p className="text-teal-700 text-sm mb-3">
            Şirket ID: <code className="bg-teal-100 px-2 py-1 rounded text-xs font-mono">{user?.company_id?.slice(0, 8)}...</code>
          </p>
          <p className="text-teal-600">
            Bu şirket hesabı ile ilgili özel destek talepleri için lütfen yukarıdaki şirket ID&apos;nizi belirtin.
          </p>
        </Card>
      )}

      {/* Solo için Hızlı Başlangıç kısayolları — sık kullanılan Solo
          işlemlerine tek tıkla erişim */}
      {isSoloPlan && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="solo-help-quick-actions">
          <Card
            className="p-4 hover:shadow-md hover:border-teal-300 transition-all cursor-pointer group"
            onClick={() => setSoloHelpArticle('new-expense')}
            data-testid="solo-help-card-new-expense"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-50 group-hover:bg-teal-100 flex items-center justify-center flex-shrink-0">
                <ReceiptIcon className="w-5 h-5 text-teal-600" />
              </div>
              <div className="min-w-0">
                <h4 className="font-semibold text-gray-900 text-sm">Yeni Harcama Nasıl Eklenir?</h4>
                <p className="text-xs text-gray-500 mt-1">6 adımlı rehber · 2 dk</p>
              </div>
            </div>
          </Card>
          <Card
            className="p-4 hover:shadow-md hover:border-teal-300 transition-all cursor-pointer group"
            onClick={() => setSoloHelpArticle('suppliers')}
            data-testid="solo-help-card-suppliers"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-50 group-hover:bg-teal-100 flex items-center justify-center flex-shrink-0">
                <BuildingIcon className="w-5 h-5 text-teal-600" />
              </div>
              <div className="min-w-0">
                <h4 className="font-semibold text-gray-900 text-sm">İşletmeler Rehberi</h4>
                <p className="text-xs text-gray-500 mt-1">Trend grafikleri + ipuçları · 3 dk</p>
              </div>
            </div>
          </Card>
          <Card
            className="p-4 hover:shadow-md hover:border-teal-300 transition-all cursor-pointer group"
            onClick={() => setSoloHelpArticle('export-report')}
            data-testid="solo-help-card-export"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-50 group-hover:bg-teal-100 flex items-center justify-center flex-shrink-0">
                <MailIcon className="w-5 h-5 text-teal-600" />
              </div>
              <div className="min-w-0">
                <h4 className="font-semibold text-gray-900 text-sm">Muhasebeciye Rapor Gönderme</h4>
                <p className="text-xs text-gray-500 mt-1">PDF/CSV e-posta akışı · 2 dk</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Solo Sık Sorulan Sorular (SSS) — akordeon. Solo kullanıcılarının
          en sık sorduğu 8 soruyu Bize Yazın'a tıklamadan yanıtlar. */}
      {isSoloPlan && (
        <Card className="p-6" data-testid="solo-faq-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
              <HelpCircleIcon className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Sık Sorulan Sorular</h2>
              <p className="text-sm text-gray-500 mt-0.5">Solo planı için en çok sorulan 8 soru</p>
            </div>
          </div>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="faq-1" data-testid="solo-faq-item-1">
              <AccordionTrigger className="text-left">
                Deneme (trial) sürem bitince harcamalarım silinir mi?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                Hayır. 14 günlük deneme süreniz bitse bile verileriniz güvenle
                saklanır. Yalnızca yeni harcama girişi kilitlenir. Solo aboneliğe
                geçtiğinizde tüm geçmiş kayıtlarınız, işletmeleriniz ve
                raporlarınız olduğu gibi karşınıza gelir. Verileriniz en az 90
                gün boyunca hesabınızda bekletilir.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-2" data-testid="solo-faq-item-2">
              <AccordionTrigger className="text-left">
                Muhasebecimin e-posta adresini nasıl değiştirebilirim?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                Ayarlar → <strong>Solo Ayarları</strong> ekranından
                &quot;Muhasebeci E-posta&quot; alanını istediğiniz zaman
                güncelleyebilirsiniz. Değişiklik anında geçerli olur — sonraki
                gönderdiğiniz PDF/CSV raporlar yeni adrese iletilir. Aynı
                ekrandan alternatif ikinci bir CC adresi de tanımlayabilirsiniz.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-3" data-testid="solo-faq-item-3">
              <AccordionTrigger className="text-left">
                USD veya EUR cinsinden harcamayı nasıl girerim?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                Yeni Harcama formunda tutarın yanındaki para birimi
                dropdown&apos;undan <strong>USD</strong> veya <strong>EUR</strong>
                seçin. Sistem harcamayı orijinal döviz cinsinden saklar ve
                raporlarda hem yerel (TRY) hem de orijinal tutar yan yana
                gösterilir. Kur değeri harcama tarihindeki günlük TCMB kuru
                üzerinden hesaplanır.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-4" data-testid="solo-faq-item-4">
              <AccordionTrigger className="text-left">
                Fişimin fotoğrafını yükleyemedim, sonradan ekleyebilir miyim?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                Evet. &quot;Tüm Harcamalarım&quot; listesinde ilgili satıra
                tıklayıp <strong>Düzenle</strong>&apos;yi seçin. Formun altında
                &quot;Fiş Yükle&quot; butonuyla fotoğrafı (JPG/PNG/PDF, maks. 10
                MB) sonradan ekleyebilirsiniz. Fişler otomatik olarak aylık
                raporunuza dahil edilir.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-5" data-testid="solo-faq-item-5">
              <AccordionTrigger className="text-left">
                Aynı fatura için 2 kere kayıt açtım, nasıl silerim?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                &quot;Tüm Harcamalarım&quot; listesinde ilgili satırın sağındaki
                sepet ikonuna tıklayın ve onaylayın. Sistem zaten aynı
                işletmeye aynı tarihte 2. bir kayıt oluştururken sizi uyarır —
                ancak yine de mükerrer geçtiyse silme işlemi geri alınamaz
                niteliktedir, dolayısıyla dikkatli davranın.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-6" data-testid="solo-faq-item-6">
              <AccordionTrigger className="text-left">
                Verilerimi Excel&apos;e nasıl aktarırım?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                &quot;Tüm Harcamalarım&quot; sayfasındaki <strong>Dışa Aktar</strong>
                butonuna tıklayıp <strong>CSV</strong> formatını seçin. Dosya
                anında bilgisayarınıza iner. CSV UTF-8 kodlamasındadır ve
                Excel&apos;de doğrudan açılır. Türkçe karakter sorunu yaşarsanız
                Excel&apos;in &quot;Metin&apos;den Al&quot; özelliğini kullanın.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-7" data-testid="solo-faq-item-7">
              <AccordionTrigger className="text-left">
                İşletme ismini yanlış yazdım, tek tek düzeltmem mi lazım?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                Hayır. <strong>İşletmeler</strong>&apos;nde işletmeye
                tıklayıp <strong>Düzenle</strong>&apos;yi seçin, yeni ismi yazın
                ve kaydedin. Bu işletmenin tüm geçmiş harcamaları tek adımda
                otomatik olarak yeni isimle güncellenir — sıfır manuel iş.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-8" data-testid="solo-faq-item-8">
              <AccordionTrigger className="text-left">
                Solo aboneliğim aylık mı yıllık mı olmalı?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                Her ikisi de mevcut. <strong>Yıllık plan</strong> genellikle 2
                ay hediye içerir (~%16 indirim). Aylık planla başlayıp deneyimi
                sevdiğinizde yıllığa geçmenizi öneririz — Ayarlar → Abonelik
                ekranından tek tıkla planı değiştirebilirsiniz, aradaki kredi
                otomatik olarak fatura üstüne yansıtılır.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>
      )}

      {/* Main Support Options — Solo'da tamamen gizli (üst-sağdaki "Bize
          Yazın" CTA butonu artık bu görevi devraldı). Diğer planlar 3
          gradient kartı (Dokümantasyon / Discord / Destek Talebi) görür. */}
      {!isSoloPlan && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Documentation */}
        <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer group bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <div className="space-y-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <BookOpenIcon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Dokümantasyon</h3>
              <p className="text-orange-100 text-sm mb-4">
                Detaylı rehberler, eğitimler, SSS ve en iyi uygulamaları keşfedin.
              </p>
              <Button 
                variant="outline" 
                className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20"
                onClick={() => navigate('/dashboard/documentation')}
              >
                Başla <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Community */}
        <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer group bg-gradient-to-br from-green-500 to-green-600 text-white">
          <div className="space-y-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <UsersIcon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Discord Topluluğu</h3>
              <p className="text-green-100 text-sm mb-4">
                Diğer kullanıcılarla bağlantı kurun ve anında yardım alın.
              </p>
              <Button 
                variant="outline" 
                className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20"
                onClick={() => window.open('https://discord.gg/quattroflow', '_blank')}
              >
                Katıl <ExternalLinkIcon className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Support Ticket */}
        <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer group bg-gradient-to-br from-blue-600 to-blue-700 text-white">
          <div className="space-y-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <HeadphonesIcon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Destek Talebi Oluştur</h3>
              <p className="text-blue-100 text-sm mb-4">
                Kişiselleştirilmiş yardım için detaylı bir destek talebi gönderin.
              </p>
              <Button 
                variant="outline" 
                className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20"
                onClick={() => setShowTicketModal(true)}
              >
                Başla <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </Card>
      </div>
      )}

      {/* Quick Actions — Solo'da gizli (Destek Taleplerim + Topluluk Forumu
          Solo bireysel deneyiminde gereksiz bulundu). Diğer planlar görür. */}
      {!isSoloPlan && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* My Support Tickets */}
        <Card className="p-6">
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <ClipboardListIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Destek Taleplerim</h3>
              <p className="text-gray-600 text-sm mb-3">
                Mevcut destek taleplerini görüntüle ve yönet
              </p>
              <Button size="sm" onClick={fetchTickets}>
                <RefreshCwIcon className="w-4 h-4 mr-2" />
                Talepleri Görüntüle
              </Button>
            </div>
          </div>
        </Card>

        {/* Community Forum */}
        <Card className="p-6">
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <MessageSquareIcon className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Topluluk Forumu
                <Badge className="ml-2 bg-green-100 text-green-800 text-xs">Yeni!</Badge>
              </h3>
              <p className="text-gray-600 text-sm mb-3">
                Yeni oluşturulan topluluk forumumuz
              </p>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => window.open('https://forum.quattroflow.com', '_blank')}
              >
                <ExternalLinkIcon className="w-4 h-4 mr-2" />
                Foruma Git
              </Button>
            </div>
          </div>
        </Card>
      </div>
      )}

      {/* Support Tickets List — Solo'da destek talebi listesi gösterilmez;
          Solo kullanıcı taleplerini e-posta üzerinden takip eder. */}
      {!isSoloPlan && tickets.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Aktif Destek Taleplerim</h2>
            <Button 
              size="sm" 
              onClick={() => setShowTicketModal(true)}
              className="flex items-center gap-2"
            >
              <PlusIcon className="w-4 h-4" />
              Yeni Talep
            </Button>
          </div>

          <div className="space-y-3">
            {tickets.map((ticket) => (
              <Card key={ticket.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="font-mono text-sm text-gray-500">
                        {ticket.ticket_number}
                      </span>
                      {getStatusBadge(ticket.status)}
                      {getPriorityBadge(ticket.priority)}
                    </div>
                    <h4 className="font-medium text-gray-900">{ticket.subject}</h4>
                    <p className="text-sm text-gray-600 flex items-center mt-1">
                      <CalendarIcon className="w-3 h-3 mr-1" />
                      {formatDate(ticket.created_at)}
                    </p>
                  </div>
                  <Button size="sm" variant="outline">
                    Detay Görüntüle
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Solo Hızlı Yardım Makale Modalı — Solo kullanıcının quick-action
          kartlarına tıkladığında adım-adım rehber gösterir. Modaldaki
          "Şimdi Dene" butonu ilgili özelliğin sayfasına yönlendirir. */}
      {isSoloPlan && (
        <Dialog
          open={!!soloHelpArticle}
          onOpenChange={(open) => { if (!open) setSoloHelpArticle(null); }}
        >
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            {soloHelpArticle && (() => {
              const article = soloHelpArticles[soloHelpArticle];
              const IconComp = article.icon;
              return (
                <>
                  <DialogHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                        <IconComp className="w-5 h-5 text-teal-600" />
                      </div>
                      <div className="flex-1 text-left">
                        <DialogTitle className="text-xl">{article.title}</DialogTitle>
                        <p className="text-xs text-gray-500 mt-1">
                          <ClockIcon className="inline w-3 h-3 mr-1" />
                          Okuma süresi: {article.readTime}
                        </p>
                      </div>
                    </div>
                  </DialogHeader>

                  <div className="space-y-5 py-4">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Adımlar</h4>
                      <ol className="space-y-2.5">
                        {article.steps.map((step, idx) => (
                          <li key={idx} className="flex gap-3 text-sm text-gray-700 leading-relaxed" data-testid={`solo-help-step-${idx}`}>
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold flex items-center justify-center mt-0.5">
                              {idx + 1}
                            </span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-1.5">
                        <SparklesIcon className="w-4 h-4" /> İpuçları
                      </h4>
                      <ul className="space-y-1.5">
                        {article.tips.map((tip, idx) => (
                          <li key={idx} className="text-sm text-amber-800 flex gap-2">
                            <span className="text-amber-500">•</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setSoloHelpArticle(null)}
                      data-testid="solo-help-close-btn"
                    >
                      Kapat
                    </Button>
                    <Button
                      className="bg-teal-600 hover:bg-teal-700"
                      onClick={() => {
                        const route = article.route;
                        setSoloHelpArticle(null);
                        navigate(route);
                      }}
                      data-testid="solo-help-cta-btn"
                    >
                      {article.cta}
                      <ArrowRightIcon className="w-4 h-4 ml-2" />
                    </Button>
                  </DialogFooter>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}

      {/* Create Ticket Modal */}
      <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Yeni Destek Talebi Oluştur</DialogTitle>
            <p className="text-sm text-gray-600">
              Sorununuzu detaylı bir şekilde açıklayın, size en kısa sürede yardımcı olalım.
            </p>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Tenant Number — Solo plan bireysel hesap olduğu için gizli;
                onun yerine e-posta rozeti gösterilir. */}
            {isSoloPlan ? (
              <div className="bg-teal-50 border border-teal-200 rounded-md p-3">
                <Label className="text-teal-900">Hesabınız</Label>
                <div className="flex items-center mt-1">
                  <span className="text-sm font-mono font-semibold text-teal-700">
                    {user?.email}
                  </span>
                  <span className="ml-2 text-xs text-teal-600">
                    (Otomatik eklendi)
                  </span>
                </div>
              </div>
            ) : (
              user?.company?.tenant_number && (
                <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
                  <Label className="text-purple-900">Kiracı Numaranız</Label>
                  <div className="flex items-center mt-1">
                    <span className="text-lg font-mono font-semibold text-purple-700">
                      #{user.company.tenant_number}
                    </span>
                    <span className="ml-2 text-xs text-purple-600">
                      (Otomatik eklendi)
                    </span>
                  </div>
                </div>
              )
            )}
            
            <div>
              <Label htmlFor="subject">Konu *</Label>
              <Input
                id="subject"
                value={ticketForm.subject}
                onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})}
                placeholder="Sorununuzun kısa özeti"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Kategori</Label>
                <select 
                  id="category"
                  value={ticketForm.category}
                  onChange={(e) => setTicketForm({...ticketForm, category: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="technical">Teknik Sorun</option>
                  <option value="billing">Faturalama</option>
                  <option value="account">Hesap İşlemleri</option>
                  <option value="feature">Özellik Talebi</option>
                  <option value="other">Diğer</option>
                </select>
              </div>
              
              <div>
                <Label htmlFor="priority">Öncelik</Label>
                <select 
                  id="priority"
                  value={ticketForm.priority}
                  onChange={(e) => setTicketForm({...ticketForm, priority: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="low">Düşük</option>
                  <option value="medium">Orta</option>
                  <option value="high">Yüksek</option>
                  <option value="critical">Kritik</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Detaylı Açıklama *</Label>
              <Textarea
                id="description"
                value={ticketForm.description}
                onChange={(e) => setTicketForm({...ticketForm, description: e.target.value})}
                placeholder="Sorununuzu mümkün olduğunca detaylı açıklayın..."
                rows={5}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowTicketModal(false)}
              disabled={processing}
            >
              İptal
            </Button>
            <Button 
              onClick={submitTicket}
              disabled={processing || !ticketForm.subject.trim() || !ticketForm.description.trim()}
            >
              {processing ? 'Oluşturuluyor...' : 'Talep Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Help;